import { Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, CircularProgress, ThemeProvider } from "@mui/material";
import { CHAT_LIST_FULL_PATH } from "@src/appV2/Chat/paths";
import { useToast } from "@src/appV2/lib";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { useEffect, useRef } from "react";
import { Route, Switch, useHistory } from "react-router-dom";

import { GuidedSupportPage } from "./GuidedSupport/Page";
import { useIsInteractiveSupportEnabled } from "./hooks/useIsInteractiveSupportEnabled";
import { GUIDED_SUPPORT_FULL_PATH, SHIFT_DETAILS_FULL_PATH, SUPPORT_FULL_PATH } from "./paths";
import { ShiftDetailsPage } from "./ShiftDetails/Page";

/**
 * Fallback component when Interactive Support is disabled.
 * Shows an interstitial while opening Zendesk, then redirects to chat list.
 *
 * Uses programmatic navigation to ensure the Zendesk call completes
 * before redirecting. Shows an error toast if Zendesk fails to open.
 */
function InteractiveSupportDisabledFallback() {
  const history = useHistory();
  const { showErrorToast } = useToast();
  const hasInitiated = useRef(false);

  useEffect(() => {
    // Prevent double execution in React strict mode
    if (hasInitiated.current) {
      return;
    }
    hasInitiated.current = true;

    async function openSupportAndRedirect() {
      try {
        await openZendeskMessaging();
      } catch {
        showErrorToast("Unable to open support chat. Redirecting...");
      } finally {
        // Always redirect to chat list, regardless of Zendesk success
        history.replace(CHAT_LIST_FULL_PATH);
      }
    }

    void openSupportAndRedirect();
  }, [history, showErrorToast]);

  return (
    <ThemeProvider theme={getTheme()}>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: theme.spacing(3),
          backgroundColor: theme.palette.background.primary,
        })}
      >
        <CircularProgress size={32} />
        <Text variant="body1" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          Opening support chat...
        </Text>
      </Box>
    </ThemeProvider>
  );
}

/**
 * Router for the Interactive Support feature.
 * Handles routing for the guided support flow and shift details pages.
 *
 * Feature gating happens here rather than at the route level.
 * When the feature is disabled, users are redirected to Zendesk messaging
 * via a deterministic redirect to the chat list.
 */
export function SupportRouter() {
  const isInteractiveSupportEnabled = useIsInteractiveSupportEnabled();

  if (!isInteractiveSupportEnabled) {
    return <InteractiveSupportDisabledFallback />;
  }

  return (
    <Switch>
      <Route exact path={GUIDED_SUPPORT_FULL_PATH} component={GuidedSupportPage} />
      <Route exact path={SHIFT_DETAILS_FULL_PATH} component={ShiftDetailsPage} />
      <Route exact path={SUPPORT_FULL_PATH} component={GuidedSupportPage} />
    </Switch>
  );
}
